import { isEmptyObject, isPresent, mapValues } from '@fcom/core/utils';
import { Category, FinnairBoundItem, FinnairServiceCatalogV3 } from '@fcom/dapi/api/models';

import { isFlight } from '../../../utils/flight.utils';

export interface FragmentQuota {
  fragmentId: string;
  quota: number;
}

interface QuotaBearingService {
  isService?: boolean;
  quota?: number | null;
  variant: string;
}

export type QuotaBearingServicesPerTraveler = Record<string, QuotaBearingService[]>;
export type QuotaBearingServiceCatalogServices = Record<string, QuotaBearingServicesPerTraveler>;

export type VariantUnits = Record<string, number>;
export type VariantUnitsPerTraveler = Record<string, VariantUnits>;
export type VariantUnitsPerFragment = Record<string, VariantUnitsPerTraveler>;

export const SUFFICIENT_QUOTA_SENTINEL = 9;

// The API caps quota at 9, where 9 stands for "9 or more", so only values below it are counts we know exactly.
export const isKnownQuota = (quota: number | null | undefined): quota is number =>
  isPresent(quota) && quota < SUFFICIENT_QUOTA_SENTINEL;

export const isLowQuota = (remaining: number | null | undefined): boolean => isKnownQuota(remaining) && remaining > 0;

export const CATEGORY_LEVEL_QUOTA_CATEGORIES: Category[] = [Category.MEAL, Category.PRIORITY];
export const VARIANT_LEVEL_QUOTA_CATEGORIES: Category[] = [Category.SPORT];

export const hasCategoryLevelQuota = (category: Category): boolean =>
  CATEGORY_LEVEL_QUOTA_CATEGORIES.includes(category);

export const hasVariantLevelQuota = (category: Category): boolean => VARIANT_LEVEL_QUOTA_CATEGORIES.includes(category);

export const variantQuotasForFragment = (services: QuotaBearingServicesPerTraveler | undefined): VariantUnits =>
  Object.values(services ?? {})
    .flat()
    .filter(isPresent)
    .filter(({ isService }) => isService)
    .reduce<VariantUnits>((quotas, { variant, quota }) => {
      if (!isKnownQuota(quota)) {
        return quotas;
      }

      return { ...quotas, [variant]: isPresent(quotas[variant]) ? Math.min(quotas[variant], quota) : quota };
    }, {});

export const variantUnitsAcrossTravelers = (unitsPerTraveler: VariantUnitsPerTraveler | undefined): VariantUnits =>
  Object.values(unitsPerTraveler ?? {}).reduce<VariantUnits>(
    (totals, unitsForTraveler) =>
      Object.entries(unitsForTraveler ?? {}).reduce(
        (all, [variant, units]) => ({ ...all, [variant]: (all[variant] ?? 0) + units }),
        totals
      ),
    {}
  );

export const remainingVariantQuota = (pool: number, selectedUnits: number, unpaidUnitsAtLoad: number): number =>
  Math.max(pool - (selectedUnits - unpaidUnitsAtLoad), 0);

export const remainingVariantQuotasPerFragment = (
  catalogServices: QuotaBearingServiceCatalogServices | undefined,
  selectedUnits: VariantUnitsPerFragment | undefined,
  unpaidUnitsAtLoad: VariantUnitsPerFragment | undefined
): Record<string, VariantUnits> => {
  return Object.entries(catalogServices ?? {}).reduce<Record<string, VariantUnits>>(
    (remaining, [fragmentId, servicesPerTraveler]) => {
      const pools = variantQuotasForFragment(servicesPerTraveler);

      if (isEmptyObject(pools)) {
        return remaining;
      }

      const selected = variantUnitsAcrossTravelers(selectedUnits?.[fragmentId]);
      const unpaid = variantUnitsAcrossTravelers(unpaidUnitsAtLoad?.[fragmentId]);

      return {
        ...remaining,
        [fragmentId]: mapValues(pools, (pool: number, variant: string) =>
          remainingVariantQuota(pool, selected[variant] ?? 0, unpaid[variant] ?? 0)
        ),
      };
    },
    {}
  );
};

export const lowestQuotaAcrossServiceVariants = (
  services: QuotaBearingServicesPerTraveler | undefined
): number | undefined => {
  const quotas = Object.values(services ?? {})
    .flat()
    .filter(Boolean)
    .filter(({ isService }) => isService)
    .map(({ quota }) => quota)
    .filter(isPresent);

  return quotas.length ? Math.min(...quotas) : undefined;
};

export const lowestQuotaAcrossFlights = (
  services: QuotaBearingServiceCatalogServices | undefined,
  initeraryFlightIdsInTravelOrder: string[]
): FragmentQuota | undefined =>
  initeraryFlightIdsInTravelOrder.reduce<FragmentQuota | undefined>((lowestFragment, flightId) => {
    const quota = lowestQuotaAcrossServiceVariants(services?.[flightId]);

    if (!isPresent(quota)) {
      return lowestFragment;
    }

    const currentFragmentQuota: FragmentQuota = { fragmentId: flightId, quota };

    if (!lowestFragment) {
      return currentFragmentQuota;
    }

    return currentFragmentQuota.quota < lowestFragment.quota ? currentFragmentQuota : lowestFragment;
  }, undefined);

export const getCategoryLevelQuotaCount = (
  serviceCatalog: FinnairServiceCatalogV3 | undefined,
  category: Category,
  bound: FinnairBoundItem
): number | undefined => {
  if (!hasCategoryLevelQuota(category) || !serviceCatalog) {
    return undefined;
  }

  const catalogForCategory = serviceCatalog.categories.find((item) => item.category === category);
  if (!catalogForCategory) {
    return 0;
  }

  const itineraryFlightId = bound.itinerary.filter(isFlight).map((flight) => flight.id);
  const serviceAvailableForBound = itineraryFlightId.some((id) => Boolean(catalogForCategory.services?.[id]));
  if (!serviceAvailableForBound) {
    return 0;
  }

  const lowestQuota = lowestQuotaAcrossFlights(catalogForCategory.services, itineraryFlightId)?.quota;

  return lowestQuota ?? SUFFICIENT_QUOTA_SENTINEL;
};
